import { RejectRequestDialogue } from "@features/cx/shared/components/RejectRequestDialogue";
import { SERVICE_REQUEST_STATUS } from "@features/cx/shared/utils/constants";
import {
  getAddressStringForConsumer,
  useCustomFilters,
} from "@features/cx/shared/utils/filters";
import { GenericTable } from "@shared/components/GenericTable";
import { useRemoteUtilityId } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { DEFAULT_PAGE_NUMBER, PAGE_SIZE } from "@shared/utils/constants";
import { XCircle } from "lucide-react";
import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "@shared/ui/use-toast";
import {
  useServiceRequests,
  useServiceRequestsExport,
  useUpdateServiceRequest,
} from "../../hooks";
import { ServiceRequest, ServiceRequestFilters } from "../../types";
import { FilterPanel } from "../filters";

const columns: TableColumn<ServiceRequest>[] = [
  { key: "requestId", header: "Service Request ID", type: "text" },
  { key: "serviceType", header: "Service Type", type: "text" },

  {
    key: "customerName",
    header: "Consumer",
    render: (value, row) => (
      <div>
        <p className="font-medium">{row.customerName}</p>
        <p className="text-xs text-muted-foreground">{row.accountNumber}</p>
      </div>
    ),
  },
  {
    key: "supportRequestType",
    header: "Category/Sub Category",
    render: (value, row) => (
      <div>
        <p className="font-medium">{row.supportRequestType ?? "NA"}</p>
        <p className="text-xs text-muted-foreground">
          {row.supportRequestSubtype ?? "NA"}
        </p>
      </div>
    ),
  },

  {
    key: "status",
    header: "Status",
    type: "badge",
    badgeConfig: {
      getVariant: (value: string) => {
        if (value === "Pending Approval") return "secondary";
        if (value === "Scheduled") return "outline";
        if (value === "In Progress") return "default";
        return "default";
      },
    },
  },
  {
    key: "createdDate",
    header: "Created On",
    render: (value, row) => (
      <div>
        <p className="font-medium">{row.createdDate ?? "NA"}</p>
        <p className="text-xs text-muted-foreground">{row.createdBy ?? "NA"}</p>
      </div>
    ),
  },
];

export const CurrentList: React.FC = () => {
  const currentTab = 2;
  const { mutateAsync: updateServiceRequest } = useUpdateServiceRequest(
    Number(0)
  );

  const [isRejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const navigate = useNavigate();
  const remoteUtilityId = useRemoteUtilityId();
  const [rejectItem, setRejectItem] = useState<ServiceRequest | null>(null);

  const previousTabRef = useRef<string | null>(null);

  // Memoize the configuration to prevent infinite re-renders
  const filterConfig = useMemo(
    () => ({
      paramNames: {
        status: "status",
        category: "category",
        search: "search_data",
        page: "page",
        name: "name",
      },
      defaults: {
        page: DEFAULT_PAGE_NUMBER,
        limit: PAGE_SIZE,
        status: [],
        category: "ALL_CATEGORIES",
        name: "ALL_CATEGORIES",
        search: "",
      },
      parsers: {
        status: (value: string) => value.split(",").map(Number),
        category: (value: string) => value,
        name: (value: string) => value,
        search: (value: string) => value.trim(),
      },
      serializers: {
        status: (value: number[]) => value.join(","),
        category: (value: string) => value,
        name: (value: string) => value,
        search: (value: string) => value.trim(),
      },
    }),
    []
  );

  // Use the configurable URL filter utility
  const {
    filters,
    page,
    handleApplyFilters,
    handleResetFilters,
    handlePageChange,
    handleSearch,
  } = useCustomFilters(filterConfig, Number(remoteUtilityId));

  // Use the hook to get current data with filters
  const { data, isLoading, error } = useServiceRequests({
    remoteUtilityId: Number(remoteUtilityId),
    activeTab: "Current",
    page,
    limit: PAGE_SIZE,
    requestType: "Service",
    searchData: filters.search_data,
    status: filters.status?.length ? filters.status.join(",") : undefined,
    name: filters.name,
  });

  const exportFilters: ServiceRequestFilters = useMemo(() => {
    return {
      remoteUtilityId: Number(remoteUtilityId),
      activeTab: "Current",
      requestType: "Service",
      searchData: filters.search_data,
      status: filters.status?.length ? filters.status.join(",") : undefined,
      name: filters.name,
    };
  }, [filters, remoteUtilityId]);

  const { exportCSV, isExporting } = useServiceRequestsExport(exportFilters);

  const handleExport = () => {
    exportCSV();
  };

  const handleRejectConfirm = async (reason: string) => {
    setIsRejecting(true);
    try {
      await updateServiceRequest({
        requestId: Number(rejectItem?.id),
        payload: {
          status: SERVICE_REQUEST_STATUS.REJECTED,
          closeDate: new Date().toISOString(),
          closureRemark: reason,
        },
      });
      toast({
        title: "Success",
        description: "Service request rejected successfully!",
        variant: "default",
      });
      setRejectDialogOpen(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reject service request.",
        variant: "destructive",
      });
    } finally {
      setIsRejecting(false);
    }
  };

  const actions: TableAction<ServiceRequest>[] = [
    {
      label: "View Details",
      onClick: (item) => {
        navigate(`/cx/services/current/${item.id}`);
      },
    },
  ];

  // Transform API data to match the table structure
  const transformedData: ServiceRequest[] =
    data?.results?.map((item: any) => ({
      id: item.id.toString(),
      requestId: item.requestNo,
      serviceType: item.utilitySupportRequest.name,
      customerName: `${item.firstName} ${item.lastName}`,
      accountNumber: item.consumerNo,
      serviceAddress: getAddressStringForConsumer(item.serviceAddress?.service),
      status: (item.statusDisplay?.toLowerCase() ||
        "pending") as ServiceRequest["status"],
      createdDate: item.createdDate,
      createdBy: item.createdUserRemoteName,
      approvalDate: item.lastModifiedDate,
      approver: item.lastModifiedUserRemoteName,
      paymentStatus: item.paymentStatus,
      estimatedFee: item.additionalData?.estTotalCost || 0,
      supportRequestType: item.supportRequestType,
      supportRequestSubtype: item.supportRequestSubtype,
    })) || [];

  const totalItems = data?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  return (
    <div>
      <GenericTable
        data={transformedData}
        columns={columns}
        rowKey="id"
        searchPlaceholder="Search by name or ID..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        pagination={{
          currentPage: page,
          totalPages,
          totalItems,
          itemsPerPage: PAGE_SIZE,
          startItem: (page - 1) * PAGE_SIZE + 1,
          endItem: Math.min(page * PAGE_SIZE, totalItems),
        }}
        onPageChange={handlePageChange}
        actions={actions}
        actionsType="dropdown"
        onExport={handleExport} // Add this line
        exportLoading={isExporting} // Add this line
        filterPanel={
          <FilterPanel
            currentFilters={filters}
            onApplyFilters={handleApplyFilters}
            onResetFilters={handleResetFilters}
            tab={currentTab}
          />
        }
      />
      <RejectRequestDialogue
        isOpen={isRejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        onReject={handleRejectConfirm}
        title="Reject Service Request"
        description="Please provide a reason for rejecting this service request."
        label="Rejection Reason"
        placeholder="Please provide a reason for rejecting this request..."
        rejectButtonText="Reject Request"
        cancelButtonText="Cancel"
        isSubmitting={isRejecting}
        icon={<XCircle className="h-5 w-5 text-red-600" />}
      />
    </div>
  );
};
